<template>
  <div class="sidebar-wrapper d-flex flex-column flex-grow-1">
    <!-- Sidebar Header / Add Button -->
    <div class="sidebar-header p-2">
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        aria-controls="sidebar-add-new-event"
        variant="primary"
        block
        class="add-event-premium-btn"
        @click="handleAddEventClick"
      >
        <div class="btn-content">
          <feather-icon icon="PlusIcon" size="18" class="mr-1" />
          <span>New Event</span>
        </div>
      </b-button>
    </div>

    <!-- Filter Section -->
    <div class="sidebar-body px-2 pb-2">
      <div class="section-divider mb-3">
        <span class="divider-text">Calendar Filters</span>
      </div>

      <div class="filter-list">
        <!-- View All Item -->
        <div 
          class="premium-filter-item mb-2" 
          :class="{ 'active': checkAll }"
          @click="toggleCheckAll"
        >
          <div class="filter-icon-box all">
            <feather-icon icon="LayersIcon" size="16" />
          </div>
          <div class="filter-info">
            <span class="filter-title">View All</span>
            <span class="filter-subtitle">Show every entry</span>
          </div>
          <div class="filter-check">
            <div class="custom-cb" :class="{ 'checked': checkAll }">
              <feather-icon v-if="checkAll" icon="CheckIcon" size="12" />
            </div>
          </div>
        </div>

        <!-- Both Item -->
        <div 
          class="premium-filter-item mb-2" 
          :class="{ 'active': showBoth }"
          @click="toggleShowBoth"
        >
          <div class="filter-icon-box both">
            <feather-icon icon="UsersIcon" size="16" />
          </div>
          <div class="filter-info">
            <span class="filter-title">Staff & Students</span>
            <span class="filter-subtitle">Shared events</span>
          </div>
          <div class="filter-check">
            <div class="custom-cb info" :class="{ 'checked': showBoth }">
              <feather-icon v-if="showBoth" icon="CheckIcon" size="12" />
            </div>
          </div>
        </div>

        <div class="sub-divider mb-2 mt-3">INDIVIDUAL</div>

        <!-- Individual Calendars -->
        <div 
          v-for="item in calendarOptions"
          :key="item.label"
          class="premium-filter-item mb-2"
          :class="[{ 'active': selectedCalendars.includes(item.label) }, `border-${item.color}`]"
          @click="toggleCalendar(item.label)"
        >
          <div class="filter-icon-box" :class="item.color">
            <feather-icon :icon="getIconForLabel(item.label)" size="16" />
          </div>
          <div class="filter-info">
            <span class="filter-title">{{ item.label }}</span>
            <span class="filter-subtitle">{{ getSubtitleForLabel(item.label) }}</span>
          </div>
          <div class="filter-check">
            <div class="custom-cb" :class="[`cb-${item.color}`, { 'checked': selectedCalendars.includes(item.label) }]">
              <feather-icon v-if="selectedCalendars.includes(item.label)" icon="CheckIcon" size="12" />
            </div>
          </div>
        </div>
      </div>
    </div>

   
  </div>
</template>

<script>
import { BButton, BImg } from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import useCalendarSidebar from "./useCalendarSidebar";

export default {
  directives: {
    Ripple,
  },
  components: {
    BButton,
    BImg,
  },
  props: {
    isEventHandlerSidebarActive: {
      type: Boolean,
      require: true,
    },
    isCalendarOverlaySidebarActive: {
      type: Boolean,
      require: true,
    },
  },
  setup(props, { emit }) {
    const handleAddEventClick = () => {
      emit("add-event");
      emit("update:is-calendar-overlay-sidebar-active", false);
    };

    const { calendarOptions, selectedCalendars, checkAll, showBoth } =
      useCalendarSidebar();

    const getIconForLabel = (label) => {
      const icons = {
        'Staff': 'UserCheckIcon',
        'Students': 'BookOpenIcon',
        'Holiday': 'SunIcon',
        'Parents': 'HeartIcon'
      };
      return icons[label] || 'CalendarIcon';
    };

    const getSubtitleForLabel = (label) => {
      const subs = {
        'Staff': 'Faculty schedules',
        'Students': 'Academic periods',
        'Holiday': 'Public & School days'
      };
      return subs[label] || 'Categorized events';
    };

    const toggleCheckAll = () => {
      checkAll.value = !checkAll.value;
    };

    const toggleShowBoth = () => {
      showBoth.value = !showBoth.value;
    };

    const toggleCalendar = (label) => {
      const current = [...selectedCalendars.value];
      const index = current.indexOf(label);
      if (index > -1) {
        current.splice(index, 1);
      } else {
        current.push(label);
      }
      selectedCalendars.value = current;
    };

    return {
      calendarOptions,
      selectedCalendars,
      checkAll,
      showBoth,
      getIconForLabel,
      getSubtitleForLabel,
      toggleCheckAll,
      toggleShowBoth,
      toggleCalendar,
      handleAddEventClick,
    };
  },
};
</script>

<style lang="scss" scoped>
.sidebar-wrapper {
  background: #ffffff;
  height: 100%;
}

.add-event-premium-btn {
  background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%) !important;
  border: none !important;
  height: 52px;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(124, 93, 250, 0.25);
  transition: all 0.3s ease;
  
  .btn-content {
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    letter-spacing: 0.5px;
    font-size: 1.05rem;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(124, 93, 250, 0.35);
  }
}

.section-divider {
  display: flex;
  align-items: center;
  position: relative;
  
  .divider-text {
    font-size: 0.72rem;
    font-weight: 800;
    color: #b4b7bd;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    background: #fff;
    padding-right: 15px;
    z-index: 1;
  }
}

.sub-divider {
  font-size: 0.65rem;
  font-weight: 800;
  color: #ccd0d5;
  letter-spacing: 2px;
}

.premium-filter-item {
  display: flex;
  align-items: center;
  padding: 12px 14px;
  background: transparent;
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.25s ease;
  border: 1px solid transparent;

  &:hover {
    background: #f8f9ff;
    transform: translateX(3px);
  }

  &.active {
    background: #f0f1ff;
    border-color: rgba(115, 103, 240, 0.1);
    
    .filter-icon-box.all { background: #7367f0; color: #fff; }
    .filter-icon-box.both { background: #00cfe8; color: #fff; }
    .filter-icon-box.primary { background: #7367f0; color: #fff; }
    .filter-icon-box.success { background: #28c76f; color: #fff; }
    .filter-icon-box.danger { background: #ea5455; color: #fff; }
    .filter-icon-box.info { background: #00cfe8; color: #fff; }
  }
}

.filter-icon-box {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 14px;
  transition: all 0.3s ease;
  
  &.all { background: #f0f1ff; color: #7367f0; }
  &.both { background: #e0f9fc; color: #00cfe8; }
  &.primary { background: #f0f1ff; color: #7367f0; }
  &.success { background: #eafbe7; color: #28c76f; }
  &.danger { background: #ffeded; color: #ea5455; }
  &.info { background: #e0f9fc; color: #00cfe8; }
}

.filter-info {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;

  .filter-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #2d3748;
    line-height: 1.2;
  }

  .filter-subtitle {
    font-size: 0.72rem;
    color: #94a3b8;
    font-weight: 500;
  }
}

.custom-cb {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 2px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  transition: all 0.2s;

  &.checked {
    background: #7367f0;
    border-color: #7367f0;
    box-shadow: 0 4px 8px rgba(115, 103, 240, 0.25);
  }

  &.checked.info {
    background: #00cfe8;
    border-color: #00cfe8;
    box-shadow: 0 4px 8px rgba(0, 207, 232, 0.25);
  }
  
  &.cb-success.checked { background: #28c76f; border-color: #28c76f; }
  &.cb-danger.checked { background: #ea5455; border-color: #ea5455; }
}

.pro-badge {
  background: #f8fafc;
  border-radius: 16px;
  padding: 1.5rem 1rem;
  border: 1px dashed #e2e8f0;
}
</style>


